import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { buildExportFilename, exportRowsToCsv, exportRowsToPdf } from '../../utils/export';
import type { ExportColumn } from '../../utils/export';

export type QuickFilter<TRow> = {
  label: string;
  filter: (row: TRow) => boolean;
};

export type SearchField =
  | { kind: 'text';   key: string; label: string }
  | { kind: 'select'; key: string; label: string; options: { value: string; label: string }[] };

export type RowAction<TRow> = {
  label: string;
  icon?: ReactNode;
  destructive?: boolean;
  /** Hide the action for some rows. Return true to render. */
  visible?: (row: TRow) => boolean;
  /**
   * Render the action but greyed-out + non-clickable for some rows (e.g. an
   * action that only applies when the device is online). Distinct from
   * `visible`, which removes the item entirely. When disabled, `disabledReason`
   * (if given) is shown as a hover tooltip so the user knows *why*.
   */
  disabled?: (row: TRow) => boolean;
  disabledReason?: (row: TRow) => string;
  onClick: (row: TRow) => void;
};

export type RouteDataGridProps<TRow extends { id: string }> = {
  entityName: string;
  queryKey: readonly unknown[];
  listFn: () => Promise<TRow[]>;
  columns: GridColDef<TRow>[];
  searchFields?: SearchField[];
  quickFilters?: QuickFilter<TRow>[];
  exportColumns: ExportColumn<TRow>[];
  reportTitle?: string;
  exportScope?: string;
  onRowClick?: (row: TRow) => void;
  emptyMessage?: string;
  /** Per-row action menu shown in the rightmost column. */
  actions?: RowAction<TRow>[];
  /** Toolbar action button (e.g. "+ Add user"). */
  primaryAction?: { label: string; onClick: () => void };
};

/**
 * Single component every list page in the portal uses. Implements the form
 * rules from docs/PORTAL_FORM_RULES.md §3 — quick filters, search panel,
 * CSV + PDF export, audit-trail-friendly rendering, em-dash for nulls.
 *
 * Stage 1.1 ships the read-only flavour. Per-row actions, the detail
 * dialog opener, and edit-mode wiring land in S1.6.
 */
export function RouteDataGrid<TRow extends { id: string }>(props: RouteDataGridProps<TRow>) {
  const {
    entityName,
    queryKey,
    listFn,
    columns,
    searchFields = [],
    quickFilters = [],
    exportColumns,
    reportTitle,
    exportScope = '',
    onRowClick,
    emptyMessage,
    actions,
    primaryAction,
  } = props;

  const allColumns = useMemo<GridColDef<TRow>[]>(() => {
    if (!actions || actions.length === 0) return columns;
    const actionsCol: GridColDef<TRow> = {
      field: '__actions',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => <RowActionsMenu row={p.row} actions={actions} />,
    };
    return [...columns, actionsCol];
  }, [columns, actions]);

  const query = useQuery({ queryKey, queryFn: listFn });

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValues, setSearchValues] = useState<Record<string, string>>({});
  const [appliedSearch, setAppliedSearch] = useState<Record<string, string>>({});
  const [activeQuickFilter, setActiveQuickFilter] = useState(0);

  const filteredRows = useMemo(() => {
    if (!query.data) return [];
    const qf = quickFilters[activeQuickFilter];
    let rows = qf ? query.data.filter(qf.filter) : query.data;
    for (const [key, raw] of Object.entries(appliedSearch)) {
      const value = raw.trim().toLowerCase();
      if (!value) continue;
      rows = rows.filter((row) => {
        const cell = (row as Record<string, unknown>)[key];
        return cell != null && String(cell).toLowerCase().includes(value);
      });
    }
    return rows;
  }, [query.data, activeQuickFilter, appliedSearch, quickFilters]);

  const activeSearchCount = Object.values(appliedSearch).filter((v) => v.trim().length > 0).length;

  const handleApply = () => {
    setAppliedSearch({ ...searchValues });
    setSearchOpen(false);
  };
  const handleClear = () => {
    setSearchValues({});
    setAppliedSearch({});
  };

  const exportTitle = reportTitle ?? entityName;

  const handleExportCsv = () => {
    if (!query.data) return;
    exportRowsToCsv(
      filteredRows as Record<string, unknown>[],
      exportColumns as ExportColumn<Record<string, unknown>>[],
      buildExportFilename(entityName, exportScope, 'csv'),
    );
  };

  const handleExportPdf = async () => {
    if (!query.data) return;
    await exportRowsToPdf(
      filteredRows as Record<string, unknown>[],
      exportColumns as ExportColumn<Record<string, unknown>>[],
      buildExportFilename(entityName, exportScope, 'pdf'),
      exportTitle,
      exportScope,
    );
  };

  return (
    <Stack spacing={2}>
      {/* Filter bar */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {primaryAction && (
            <Button size="small" variant="contained" onClick={primaryAction.onClick}>
              {primaryAction.label}
            </Button>
          )}
          {quickFilters.length > 0 && (
            <ButtonGroup size="small">
              {quickFilters.map((qf, i) => (
                <Button
                  key={qf.label}
                  variant={i === activeQuickFilter ? 'contained' : 'outlined'}
                  onClick={() => setActiveQuickFilter(i)}
                >
                  {qf.label}
                </Button>
              ))}
            </ButtonGroup>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {searchFields.length > 0 && (
            <Button
              size="small"
              variant={searchOpen ? 'contained' : 'outlined'}
              onClick={() => setSearchOpen((v) => !v)}
              endIcon={
                activeSearchCount > 0 ? (
                  <Chip size="small" label={activeSearchCount} color="primary" />
                ) : null
              }
            >
              Search
            </Button>
          )}

          <Button size="small" variant="outlined" onClick={handleExportCsv} disabled={!query.data}>
            Export CSV
          </Button>
          <Button size="small" variant="outlined" onClick={handleExportPdf} disabled={!query.data}>
            Export PDF
          </Button>
        </Stack>

        {searchOpen && searchFields.length > 0 && (
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={handleClear}>Clear All</Button>
              <Button size="small" variant="contained" onClick={handleApply}>
                Apply
              </Button>
            </Stack>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: 1.5,
            }}>
              {searchFields.map((field) => (
                <SearchInput
                  key={field.key}
                  field={field}
                  value={searchValues[field.key] ?? ''}
                  onChange={(v) => setSearchValues((prev) => ({ ...prev, [field.key]: v }))}
                  onEnter={handleApply}
                />
              ))}
            </Box>
          </Stack>
        )}
      </Paper>

      {/* Table / states */}
      {query.isLoading ? (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={20} />
          <Typography>Loading {entityName.toLowerCase()}…</Typography>
        </Stack>
      ) : query.error ? (
        <ErrorAlert action={<Button onClick={() => query.refetch()}>Retry</Button>}>
          {(query.error as { message?: string })?.message ?? 'Failed to load data.'}
        </ErrorAlert>
      ) : filteredRows.length === 0 ? (
        <Alert severity="info">{emptyMessage ?? `No ${entityName.toLowerCase()}.`}</Alert>
      ) : (
        <Paper variant="outlined" sx={{ height: 'calc(100vh - 300px)', minHeight: 360 }}>
          <DataGrid
            rows={filteredRows}
            columns={allColumns}
            disableRowSelectionOnClick
            onRowClick={(params: GridRowParams<TRow>) => onRowClick?.(params.row)}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[10, 25, 50, 100]}
            // Auto-size every column on first render based on actual cell
            // content + header text, then fan out across the available
            // width. Replaces the per-page hand-tuned `width: NNN` values
            // that drifted: long QR-IDs got truncated, em-dashes occupied
            // 50% of the screen, etc. Pages can still set `flex:` /
            // `minWidth:` on a column to lock its preferred share —
            // autosize respects both.
            autosizeOnMount
            autosizeOptions={{
              includeHeaders: true,
              includeOutliers: true,
              expand: true,
              outliersFactor: 1.5,
            }}
            // Force vertical centring on every cell. Without this a custom
            // renderCell that returns a <Box>/<Stack> shorter than the row
            // height anchors top, while plain-text cells centre — and the
            // misalignment shows up as wobbly columns. House rule lives in
            // memory feedback_grid_consistent_alignment.
            sx={{
              border: 0,
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </Paper>
      )}
    </Stack>
  );
}

function RowActionsMenu<TRow>({
  row, actions,
}: {
  row: TRow;
  actions: RowAction<TRow>[];
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const visible = actions.filter((a) => a.visible?.(row) ?? true);
  if (visible.length === 0) return null;

  return (
    <>
      <Button
        size="small"
        sx={{ minWidth: 32, p: 0.5 }}
        onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
      >
        <MoreVertIcon fontSize="small" />
      </Button>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        {visible.map((a) => {
          const isDisabled = a.disabled?.(row) ?? false;
          const reason = isDisabled ? a.disabledReason?.(row) : undefined;
          const item = (
            <MenuItem
              key={a.label}
              disabled={isDisabled}
              onClick={() => { setAnchor(null); a.onClick(row); }}
              sx={{ color: a.destructive && !isDisabled ? 'error.main' : undefined }}
            >
              {a.icon && <Box sx={{ mr: 1, display: 'flex' }}>{a.icon}</Box>}
              {a.label}
            </MenuItem>
          );
          // A disabled MenuItem swallows pointer events, so wrap it in a <span>
          // for the Tooltip to have a hoverable target and explain *why*.
          return reason
            ? <Tooltip key={a.label} title={reason}><span style={{ display: 'block' }}>{item}</span></Tooltip>
            : item;
        })}
      </Menu>
    </>
  );
}

function SearchInput({
  field, value, onChange, onEnter,
}: {
  field: SearchField;
  value: string;
  onChange: (v: string) => void;
  onEnter: () => void;
}) {
  if (field.kind === 'select') {
    return (
      <TextField
        select
        SelectProps={{ native: true }}
        size="small"
        label={field.label}
        // Native <select> always has a "value" (the empty option), so MUI's
        // auto-shrink logic doesn't kick in. Force the label to stay in
        // the floating-small position so it doesn't overlap the option text.
        InputLabelProps={{ shrink: true }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
      >
        <option value="">Any</option>
        {field.options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </TextField>
    );
  }
  return (
    <TextField
      size="small"
      label={field.label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') onEnter(); }}
      fullWidth
    />
  );
}
